import React, {useEffect, useRef, useState} from 'react';

import cs from 'classnames';
import get from 'lodash/get';
import {useSelector} from 'react-redux';
import {Link} from 'react-router';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {CurrentUserSelector} from 'app/current-user/api';
import {CollapsableFolderPickerForm} from 'app/folders/components/CollapsableFolderPickerForm';
import {ClonePayloadType} from 'app/general/actions';
import {
  createFormErrorsContext,
  FormControlGroup,
  FormErrorsProps,
} from 'app/general/forms';
import {useDuplicateKnowledgebase} from 'app/kbs/api';
import {useDuplicatePolicy} from 'app/policies/api';
import {useDuplicateProcedure} from 'app/procedures/api';
import {useDuplicateProcess} from 'app/process/api';
import {useDuplicateQuiz} from 'app/quizzes/api';
import {CollapsableTeamPickerForm} from 'app/teams/components/CollapsableTeamPickerForm';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import {
  DetailPolicySerializedType,
  FolderSerializedType,
  KnowledgebaseSerializedType,
  ProcedureSerializedType,
  QuizSerializedType,
  TaskJobSerializedType,
  TeamSerializedType,
} from 'sweet-types';

interface ClonedDocType {
  html_url: string;
  name: string;
}

interface Props extends FormErrorsProps {
  contentType:
    | 'procedure'
    | 'policy'
    | 'proceduregroup'
    | 'quiz'
    | 'knowledgebase';
  documentURL: string;
  documentTitle: string;
  isOpen: boolean;
  showTeam?: boolean;
  showFolder?: boolean;
  onClose: () => unknown;
  onClone: (doc?: ClonedDocType) => unknown;
}

function BaseCloneDocumentModal({
  contentType,
  documentURL,
  documentTitle,
  isOpen,
  showTeam = true,
  showFolder = true,
  onClose,
  onClone,
  captureFormErrorsFromRejection,
}: Props) {
  const [status, setStatus] = useState<
    'start' | 'cloning' | 'success' | 'error'
  >('start');
  const [title, setTitle] = useState<string>(
    gt('%(documentTitle)s - Copy', {documentTitle})
  );
  const [teams, setTeams] = useState<Array<TeamSerializedType>>([]);
  const [folders, setFolders] = useState<Array<FolderSerializedType>>([]);
  const [clonedDoc, setClonedDoc] = useState<null | ClonedDocType>(null);
  const [duplicateProcedures, setDuplicateProcedures] = useState<boolean>(true);
  const isSuperManager = useSelector(
    (state) => CurrentUserSelector(state).data!.is_super_manager
  );
  const canClone = isSuperManager || teams.length > 0;

  const ct = getContentType(contentType);
  const docType = capitalizeFirstLetter(ct.name);
  const useAsyncActions = {
    quiz: useDuplicateQuiz,
    knowledgebase: useDuplicateKnowledgebase,
    policy: useDuplicatePolicy,
    procedure: useDuplicateProcedure,
    proceduregroup: useDuplicateProcess,
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputRef.current != null && inputRef.current.value !== '') {
      inputRef.current.select();
    }
  }, []);

  const [duplicateDoc] = useAsyncActions[contentType]();

  const clone = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!canClone) {
      return;
    }

    setStatus('cloning');

    let payload: ClonePayloadType = {
      url: documentURL,
      contentType: contentType,
      name: title,
      teams: teams.map((team) => team.url),
      folders: folders.map((folder) => folder.url),
    };

    if (contentType === 'proceduregroup') {
      payload['duplicate_procedures'] = duplicateProcedures;
    }

    return duplicateDoc(payload)
      .unwrap()
      .then(
        (
          result:
            | ProcedureSerializedType
            | DetailPolicySerializedType
            | QuizSerializedType
            | TaskJobSerializedType
            | KnowledgebaseSerializedType
        ) => {
          let document: ClonedDocType | null;
          if ('result' in result) {
            document = result.result;
          } else {
            document = result;
          }
          setStatus('success');
          setClonedDoc(document);
          onClone(document);
        },
        (err) => {
          if (get(err, 'response.status') === 400) {
            setStatus('start');
            captureFormErrorsFromRejection(err);
          } else {
            setStatus('error');
          }
          return err;
        }
      );
  };

  return (
    <TabbedModal
      show={isOpen}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Duplicate %(docType)s', {docType}),
          icon: ct.icon,
          content: (
            <form onSubmit={clone}>
              {status === 'start' ? (
                <>
                  <FormControlGroup field="name">
                    <label htmlFor="cloned-document-title">
                      {gt('New %(ctName)s title', {ctName: ct.name})}
                    </label>
                    <input
                      ref={inputRef}
                      placeholder={gt('%(docType)s Title', {docType})}
                      id="cloned-document-title"
                      className="form-control"
                      autoFocus={true}
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </FormControlGroup>
                  {contentType === 'proceduregroup' ? (
                    <FormControlGroup
                      className="form-check"
                      field="duplicate_procedures"
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={duplicateProcedures}
                        onChange={() =>
                          setDuplicateProcedures(!duplicateProcedures)
                        }
                        id="duplicate-procedures-field"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="duplicate-procedures-field"
                      >
                        {gt('Duplicate associated procedures as well')}
                      </label>
                    </FormControlGroup>
                  ) : null}
                  {showTeam && (
                    <CollapsableTeamPickerForm
                      small={true}
                      selected={teams}
                      onChange={setTeams}
                      counterPhrase={(numTeams) =>
                        ngt(
                          'The %2$s will be added to %1$d team',
                          'The %2$s will be added to %1$d teams',
                          numTeams,
                          gt('cloned %(ctName)s', {ctName: ct.name})
                        )
                      }
                    />
                  )}
                  {showFolder && (
                    <CollapsableFolderPickerForm
                      onChange={(folders: Array<FolderSerializedType>) =>
                        Promise.resolve(setFolders(folders))
                      }
                    />
                  )}
                </>
              ) : status === 'cloning' ? (
                <div className="text-center">
                  <div className="progress my-3">
                    <div className="px-1 progress-bar progress-bar-striped progress-bar-animated w-100">
                      {gt('Cloning %s...', docType)}
                    </div>
                  </div>
                  <p>
                    {gt(
                      'This could take some seconds, your %(ctName)s will appear here when finished',
                      {ctName: ct.name}
                    )}
                    .
                  </p>
                </div>
              ) : status === 'success' && clonedDoc != null ? (
                <div className="text-center">
                  <p className="mb-1">
                    {gt('The %(ctName)s has been created:', {ctName: ct.name})}
                  </p>
                  <p className="font-weight-bold">{clonedDoc.name}</p>
                  <p>
                    <Link to={clonedDoc.html_url} className="btn btn-primary">
                      {gt('View %(docType)s', {docType})}
                    </Link>
                  </p>
                </div>
              ) : status === 'error' ? (
                <div className="text-danger text-center">
                  <p className="font-weight-bold">
                    {gt('There was an error cloning the %(ctName)s.', {
                      ctName: ct.name,
                    })}
                  </p>
                  <p>{gt('Please try again later')}.</p>
                </div>
              ) : null}

              <StyledModalFooter>
                {status === 'start' ? (
                  <>
                    <button
                      onClick={onClose}
                      type="button"
                      className="btn btn-link ml-auto"
                    >
                      <i className="fa fa-times" /> {gt('Cancel')}
                    </button>
                    <LazyTooltip
                      hide={canClone}
                      tooltip={gt(
                        'Please select at least one team to add the cloned %(ctName)s',
                        {ctName: ct.name}
                      )}
                    >
                      <button
                        className={cs('btn btn-primary', {disabled: !canClone})}
                        type="submit"
                      >
                        {gt('Duplicate')}
                      </button>
                    </LazyTooltip>
                  </>
                ) : (
                  <button onClick={onClose} className="btn btn-link ml-auto">
                    {gt('Close')}
                  </button>
                )}
              </StyledModalFooter>
            </form>
          ),
        },
      ]}
    />
  );
}

export const CloneDocumentModal = createFormErrorsContext<Props>(
  BaseCloneDocumentModal
);
